import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const errorSchema = z.object({ message: z.string() });
const prosemirrorJsonSchema = z.record(z.string(), z.unknown());

export const docIdSchema = z.string().regex(/^doc_[0-9A-Za-z]{22}$/);
export const projectIdSchema = z.string().regex(/^proj_[0-9A-Za-z]{22}$/);

export const docStatusValues = ['active', 'archived'] as const;
export const docStatusSchema = z.enum(docStatusValues);

export const docAuthorSchema = z.object({
  id: z.string().min(1),
  display_name: z.string().min(1),
});

export const docProjectSchema = z.object({
  id: projectIdSchema,
  name: z.string().min(1),
});

export const docListObjectSchema = z.object({
  id: docIdSchema,
  title: z.string().min(1),
  status: docStatusSchema,
  author: docAuthorSchema,
  project: docProjectSchema.nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
});

export const docListResponseSchema = z.object({
  type: z.literal('list'),
  list_type: z.literal('doc'),
  objects: z.array(docListObjectSchema),
});

export const listDocsQuerySchema = z.object({
  project_id: projectIdSchema.optional(),
  status: docStatusSchema.optional(),
});

export const createDocRequestSchema = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  prosemirror_json: prosemirrorJsonSchema.optional(),
  project_id: projectIdSchema.nullable().optional(),
});

export const createDocResponseSchema = z.object({
  id: docIdSchema,
  created_at: z.string().datetime(),
});

export const updateDocRequestSchema = z
  .object({
    title: z.string().trim().min(1).max(300).optional(),
    prosemirror_json: prosemirrorJsonSchema.optional(),
    project_id: projectIdSchema.nullable().optional(),
    status: docStatusSchema.optional(),
  })
  .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be provided',
  });

export const updateDocResponseSchema = z.object({
  id: docIdSchema,
  updated_at: z.string().datetime(),
});

export const getDocResponseSchema = z.object({
  id: docIdSchema,
  title: z.string().min(1),
  prosemirror_json: prosemirrorJsonSchema,
  status: docStatusSchema,
  author: docAuthorSchema,
  project: docProjectSchema.nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
});

export const docsContract = c.router({
  listDocs: {
    method: 'GET',
    path: '/docs',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    query: listDocsQuerySchema,
    responses: {
      200: docListResponseSchema,
      401: errorSchema,
      403: errorSchema,
    },
    summary:
      'List docs in the authenticated user’s organization. Optional project_id filter scopes to a single project.',
  },
  createDoc: {
    method: 'POST',
    path: '/docs',
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: createDocRequestSchema,
    responses: {
      201: createDocResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
      422: errorSchema,
    },
    summary:
      'Create a doc. If project_id is provided, the doc is scoped to that project; otherwise it lives at the workspace level.',
  },
  getDoc: {
    method: 'GET',
    path: '/docs/:docId',
    pathParams: z.object({
      docId: docIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    responses: {
      200: getDocResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Get a doc by id with its rich-text body and parent project (if any)',
  },
  updateDoc: {
    method: 'PATCH',
    path: '/docs/:docId',
    pathParams: z.object({
      docId: docIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: updateDocRequestSchema,
    responses: {
      200: updateDocResponseSchema,
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
      422: errorSchema,
    },
    summary: 'Update a doc title, rich-text body, status, or parent project.',
  },
  deleteDoc: {
    method: 'DELETE',
    path: '/docs/:docId',
    pathParams: z.object({
      docId: docIdSchema,
    }),
    headers: z.object({
      authorization: z.string().min(1).optional(),
    }),
    body: z.object({}).optional(),
    responses: {
      204: z.object({}),
      401: errorSchema,
      403: errorSchema,
      404: errorSchema,
    },
    summary: 'Hard-delete a doc and any mention references it owns',
  },
});

export type DocStatus = z.infer<typeof docStatusSchema>;
export type DocAuthor = z.infer<typeof docAuthorSchema>;
export type DocProject = z.infer<typeof docProjectSchema>;
export type DocListObject = z.infer<typeof docListObjectSchema>;
export type DocListResponse = z.infer<typeof docListResponseSchema>;
export type ListDocsQuery = z.infer<typeof listDocsQuerySchema>;
export type CreateDocRequest = z.infer<typeof createDocRequestSchema>;
export type CreateDocResponse = z.infer<typeof createDocResponseSchema>;
export type UpdateDocRequest = z.infer<typeof updateDocRequestSchema>;
export type UpdateDocResponse = z.infer<typeof updateDocResponseSchema>;
export type GetDocResponse = z.infer<typeof getDocResponseSchema>;
